import LinearProgress from '@mui/joy/LinearProgress';

import { decompressFromEncodedURIComponent } from 'lz-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { mutate } from 'swr';

import Breadcrumbs from '@/components/Breadcrumbs';
import { GithubChip } from '@/components/GithubChip';
import { useTab } from '@/components/Tabs/useTab';
import { FetcherParams } from '@/data/fetcher';
import { useStateFromSearchParams } from '@/hooks/useStateFromSearchParams';
import {
  ExperimentViewOverridesSchema,
  ExperimentViewOverridesZodSchema,
} from '@/types/schema';
import { apiFeedbackConfigsPath } from '@/utils/constants';
import {
  LogPageContext,
  LogPageContextType,
} from '@/utils/datadog/LogPageContext';
import { useInfoLogger } from '@/utils/datadog/useInfoLogger';
import { usePageTracking } from '@/utils/datadog/usePageTracking';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { PageTitle } from '../../components/PageTitle';
import {
  useComparativeExperiments,
  useDataset,
  useDatasetFeedbackDelta,
  useExperimentView,
  useOrganizationId,
  useSessions,
} from '../../hooks/useSwr';
import { RenderModeContext } from '../Graph/src/control/state';
import { EditableExperimentName } from '../Playground/PlaygroundHome/EditableExperimentName';
import { DatasetSessionCompareBarCharts } from './components/CompareBarCharts/DatasetSessionCompareBarCharts';
import { CompareExperimentTabs } from './components/CompareExperimentTabs/types';
import { DatasetSessionCompareTable } from './components/DatasetSessionCompareTable';
import { DatasetSessionControlHeader } from './components/DatasetSessionControlHeader';
import { ExperimentsGroupByTable } from './components/ExperimentsGroupByTable/ExperimentsGroupByTable';
import { useGroupByMetadata } from './components/ExperimentsGroupByTable/useGroupByMetadata';
import { useExamples } from './hooks/useExamples';
import { anyExampleHasAttachments } from './utils/anyExampleHasAttachments';
import { useComparisonViewDisplaySettings } from './utils/useComparisonViewDisplaySettings';

const DATADOG_PAGE_NAME = 'dataset_session_compare';

const SELECTED_FEEDBACK_KEY_LOCAL_STORAGE_KEY_PREFIX =
  'dataset_session_compare_selected_feedback_key';

const unserializeFilters = (filterString) => {
  const decompressedString = decompressFromEncodedURIComponent(filterString);
  const filters = {};
  const params = new URLSearchParams(decompressedString);

  for (const [key, value] of params) {
    if (key && value) {
      filters[key] = value.split(';');
    }
  }

  return filters;
};

export const DatasetSessionCompare = () => {
  const { datasetId, datasetShareToken } = useParams();

  const organizationId = useOrganizationId();

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedSessionsRaw = searchParams.get('selectedSessions');
  const sessionIds = useMemo(
    () =>
      selectedSessionsRaw
        ? decodeURIComponent(selectedSessionsRaw).split(',').filter(Boolean)
        : [],
    [selectedSessionsRaw]
  );

  const comparativeExperiment = searchParams.get('comparativeExperiment');

  const filterParamsString = searchParams.get('filters');
  const filterParamsFromSearchParams = filterParamsString
    ? unserializeFilters(filterParamsString)
    : {};
  const [filterParams, setFilterParamsState] = useState(
    filterParamsFromSearchParams
  );

  const dataset = useDataset(datasetId, { datasetShareToken });
  const { data: defaultExperimentView, isLoading: isExperimentViewLoading } =
    useExperimentView(datasetId, {
      revalidateOnFocus: false,
    });

  const [localStorageExperimentView] = useLocalStorageState<
    ExperimentViewOverridesSchema | undefined
  >(`ls:localExperimentViewOverride-${datasetId}`, undefined);

  // Local storage hook is not reactive when underlying local storage value changes,
  // so we wrap the locally modified value
  const [localExperimentView, setLocalExperimentView] = useState(
    localStorageExperimentView
  );

  let experimentView = localExperimentView ?? defaultExperimentView;
  try {
    experimentView = ExperimentViewOverridesZodSchema.parse(experimentView);
  } catch (error) {
    experimentView = defaultExperimentView;
  }

  const filters: Record<string, string[]> = Object.entries(filterParams).reduce(
    (acc, [key, value]) => {
      if (sessionIds.includes(key)) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );

  const [addedSessions, setAddedSessions] = useState<string[]>([]);

  const [regressionsFilterFeedbackKey, setRegressionsFilterFeedbackKey] =
    useLocalStorageState<string | null>(
      `ls:${SELECTED_FEEDBACK_KEY_LOCAL_STORAGE_KEY_PREFIX}:${datasetId}`,
      null
    );
  const [filteringRegressionsOnSession, setFilteringRegressionsOnSession] =
    useState<string | null>(null);
  const [
    regressionsFilteringToImprovements,
    setRegressionsFilteringToImprovements,
  ] = useState<boolean>(false);
  const [selectedBaselineSessionIdState, setSelectedBaselineSessionIdState] =
    useStateFromSearchParams('baseline', 'undefined');
  const selectedBaselineSessionId =
    selectedBaselineSessionIdState === 'undefined' ||
    selectedBaselineSessionIdState === 'null'
      ? undefined
      : selectedBaselineSessionIdState;
  const setSelectedBaselineSessionId = useCallback(
    (sessionId?: string | null) => {
      setSelectedBaselineSessionIdState(
        sessionId === undefined
          ? 'undefined'
          : sessionId === null
          ? 'null'
          : sessionId
      );
    },
    [setSelectedBaselineSessionIdState]
  );

  const feedbackDelta = useDatasetFeedbackDelta(
    datasetId ?? null,
    (selectedBaselineSessionId || comparativeExperiment) &&
      regressionsFilterFeedbackKey
      ? {
          baseline_session_id: comparativeExperiment
            ? sessionIds[0]
            : selectedBaselineSessionId ?? '',
          comparison_session_ids: sessionIds.filter(
            (id) =>
              id !==
              (comparativeExperiment
                ? sessionIds[0]
                : selectedBaselineSessionId)
          ),
          comparative_experiment_id: comparativeExperiment ?? undefined,
          feedback_key: regressionsFilterFeedbackKey,
          filters,
        }
      : null,
    { datasetShareToken }
  );

  const filtersToFetch = useMemo(() => {
    if (
      !regressionsFilterFeedbackKey ||
      (!selectedBaselineSessionId && !comparativeExperiment) ||
      !filteringRegressionsOnSession ||
      sessionIds.length < 2
    ) {
      return filters;
    }
    const newFilters = { ...filters };
    if (regressionsFilterFeedbackKey && filteringRegressionsOnSession) {
      const improvedExamples = regressionsFilteringToImprovements
        ? feedbackDelta.data?.feedback_deltas?.[filteringRegressionsOnSession]
            ?.improved_examples
        : feedbackDelta.data?.feedback_deltas?.[filteringRegressionsOnSession]
            ?.regressed_examples;
      const filter = `in(reference_example_id, [${improvedExamples
        ?.map((e) => `'${e}'`)
        .join(', ')}])`;
      if (improvedExamples) {
        if (!Object.keys(filters).includes(sessionIds[0])) {
          newFilters[sessionIds[0]] = [filter];
        } else {
          const firstSession = Object.keys(filters)[0];
          newFilters[firstSession] = newFilters[firstSession].concat([filter]);
        }
      }
    }
    return newFilters;
  }, [
    comparativeExperiment,
    feedbackDelta.data?.feedback_deltas,
    filteringRegressionsOnSession,
    filters,
    regressionsFilterFeedbackKey,
    regressionsFilteringToImprovements,
    selectedBaselineSessionId,
    sessionIds,
  ]);

  const {
    examples,
    areExamplesLoading,
    setExamplesSize,
    mutateInfiniteExamplesSwr,
    hasMoreToLoad,
    isNextPageValidating,
  } = useExamples({
    datasetId,
    sessionIds,
    filtersToFetch,
    comparativeExperiment,
    datasetShareToken,
  });

  const sessions = useSessions(
    {
      id: sessionIds,
      facets: true,
      include_stats: true,
      ...(datasetId ? { reference_dataset: [datasetId] } : {}),
    },
    { datasetShareToken },
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const [hoveredSession, setHoveredSession] = useState<number | null>(null);

  const pageTitleText =
    sessionIds.length === 1 && sessions.data?.rows?.[0]
      ? sessions.data.rows[0].name
      : comparativeExperiment
      ? 'Viewing Pairwise Experiment'
      : `Comparing ${sessionIds.length} Experiments`;

  const pageTitle =
    sessionIds.length === 1 && sessions.data?.rows?.[0] ? (
      <div className="flex items-center gap-2">
        <EditableExperimentName
          sessionId={sessions.data?.rows?.[0]?.id}
          sessionName={sessions.data?.rows?.[0]?.name}
          textClassProp="text-2xl text-secondary font-medium truncate max-w-full"
          inputTextClassProp="text-2xl text-secondary font-medium truncate max-w-full w-full rounded-md"
        />
        <GithubChip
          gitMetadata={sessions.data?.rows?.[0]?.extra?.metadata?.git}
          className="text-sm"
        />
      </div>
    ) : (
      pageTitleText
    );

  const aggregateFeedbackKeys = useMemo(() => {
    return !sessions.isLoading
      ? sessions.data?.rows?.reduce((acc, session) => {
          const feedbackStats = session.feedback_stats;
          if (feedbackStats) {
            Object.keys(feedbackStats).forEach((key) => {
              if (!acc.includes(key)) {
                acc.push(key);
              }
            });
          }
          return acc;
        }, [] as string[]) ?? []
      : undefined;
  }, [sessions.data?.rows, sessions.isLoading]);

  const aggregateSessionLevelFeedbackKeys = useMemo(
    () =>
      !sessions.isLoading
        ? sessions.data?.rows?.reduce((acc, session) => {
            const feedbackStats = session.session_feedback_stats;
            if (feedbackStats) {
              Object.keys(feedbackStats).forEach((key) => {
                if (!acc.includes(key)) {
                  acc.push(key);
                }
              });
            }
            return acc;
          }, [] as string[]) ?? []
        : undefined,
    [sessions.data?.rows, sessions.isLoading]
  );

  const aggregateMetadataKeys = useMemo(() => {
    return !sessions.isLoading
      ? sessions.data?.rows?.reduce((acc, session) => {
          const metadata = session.extra?.metadata;
          Object.keys(metadata ?? {}).forEach((key) => {
            if (!acc.includes(key)) {
              acc.push(key);
            }
          });
          return acc;
        }, [] as string[]) ?? []
      : undefined;
  }, [sessions.data?.rows, sessions.isLoading]);

  useEffect(() => {
    if (
      sessionIds.length > 0 &&
      selectedBaselineSessionId === undefined &&
      sessions.data?.rows &&
      !comparativeExperiment
    ) {
      setSelectedBaselineSessionId(sessionIds[0]);
    }
  }, [
    sessionIds,
    selectedBaselineSessionId,
    sessions.data?.rows,
    setSelectedBaselineSessionId,
    comparativeExperiment,
  ]);

  const displaySettingsState = useComparisonViewDisplaySettings({
    aggregateFeedbackKeys,
    aggregateSessionLevelFeedbackKeys,
    examples,
    searchParams,
    setSearchParams,
    isSingleSession: sessionIds.length === 1,
  });
  const { language, selectedCharts, setSelectedCharts } = displaySettingsState;

  const selectedComparativeExperimentSwr = useComparativeExperiments(
    datasetId ?? null,
    comparativeExperiment
      ? {
          id: [comparativeExperiment],
        }
      : null,
    { datasetShareToken }
  );
  const selectedComparativeExperiment =
    selectedComparativeExperimentSwr.data?.[0];

  useEffect(() => {
    if (
      aggregateFeedbackKeys &&
      aggregateFeedbackKeys.length > 0 &&
      (!regressionsFilterFeedbackKey ||
        !aggregateFeedbackKeys.includes(regressionsFilterFeedbackKey)) &&
      !comparativeExperiment
    ) {
      setRegressionsFilterFeedbackKey(aggregateFeedbackKeys[0]);
    }
    if (comparativeExperiment) {
      const feedbackKey = Object.keys(
        selectedComparativeExperiment?.feedback_stats ?? {}
      )[0];
      if (feedbackKey) {
        setRegressionsFilterFeedbackKey(feedbackKey);
      }
    }
  }, [
    aggregateFeedbackKeys,
    comparativeExperiment,
    regressionsFilterFeedbackKey,
    selectedComparativeExperiment?.feedback_stats,
    setRegressionsFilterFeedbackKey,
  ]);

  const sessionIdsSorted = useMemo(() => {
    const otherSessionsSorted = sessionIds.filter(
      (id) => id !== selectedBaselineSessionId
    );
    otherSessionsSorted.sort((a, b) => {
      if (selectedComparativeExperiment) {
        return a === selectedComparativeExperiment.experiments_info[0].id
          ? -1
          : 1;
      }
      const aSession = sessions.data?.rows?.find((s) => s.id === a);
      const bSession = sessions.data?.rows?.find((s) => s.id === b);
      if (!aSession?.test_run_number && !bSession?.test_run_number) {
        return 0;
      } else if (!aSession?.test_run_number) {
        return 1;
      } else if (!bSession?.test_run_number) {
        return -1;
      }
      return aSession.test_run_number - bSession.test_run_number;
    });
    return (
      selectedBaselineSessionId && sessionIds.length > 1
        ? [selectedBaselineSessionId, ...otherSessionsSorted]
        : otherSessionsSorted
    ).filter((id) => sessionIds.includes(id));
  }, [
    sessionIds,
    selectedBaselineSessionId,
    selectedComparativeExperiment,
    sessions.data?.rows,
  ]);

  useEffect(() => {
    if (sessionIdsSorted.length <= 1) {
      setSelectedBaselineSessionId(undefined);
    } else if (
      selectedBaselineSessionId &&
      !sessionIdsSorted.includes(selectedBaselineSessionId)
    ) {
      setSelectedBaselineSessionId(sessionIdsSorted[0]);
    }
  }, [
    sessions.data?.rows,
    sessionIdsSorted,
    selectedBaselineSessionId,
    setSelectedBaselineSessionId,
  ]);

  const totalFiltersApplied = Object.values(filters).reduce(
    (acc, value) => acc + value.length,
    0
  );

  const isRegressionTrackingUIEnabled =
    regressionsFilterFeedbackKey != null &&
    selectedBaselineSessionId != null &&
    !comparativeExperiment;

  const { datadogExpandedMetadata, datadogSimpleMetadata } = useMemo(() => {
    // Metadata available on load
    const datadogSimpleMetadata = {
      datasetId,
      datasetShareToken,
      organizationId,
      numSessions: sessionIds.length,
      filters,
      totalFiltersApplied,
      numSessionsFilteredOn: Object.keys(filters).length,
    };
    if (
      sessions.isLoading ||
      sessions.isValidating ||
      !sessions.data?.rows ||
      selectedCharts === null
    )
      return {
        datadogSimpleMetadata,
        datadogExpandedMetadata: null,
      };
    // Metadata available after data is loaded
    const datadogExpandedMetadata = {
      ...datadogSimpleMetadata,
      numRunLevelFeedbackKeys: aggregateFeedbackKeys?.length,
      numSessionLevelFeedbackKeys: aggregateSessionLevelFeedbackKeys?.length,
      numChartsShown:
        sessionIds.length > 1 ? selectedCharts?.length ?? 0 : null,
      chartsShown: selectedCharts,
      language,
      isRegressionTrackingUIEnabled,
    };
    return {
      datadogSimpleMetadata,
      datadogExpandedMetadata,
    };
  }, [
    aggregateFeedbackKeys,
    aggregateSessionLevelFeedbackKeys,
    sessionIds,
    language,
    isRegressionTrackingUIEnabled,
    sessions.isLoading,
    sessions.isValidating,
    sessions.data?.rows,
    selectedCharts,
    datasetId,
    datasetShareToken,
    organizationId,
    filters,
    totalFiltersApplied,
  ]);

  const pageTrackingMetadata: LogPageContextType = useMemo(
    () => [DATADOG_PAGE_NAME, datadogSimpleMetadata],
    [datadogSimpleMetadata]
  );
  const infoLoggerMetadata: LogPageContextType = useMemo(
    () => [DATADOG_PAGE_NAME, datadogExpandedMetadata],
    [datadogExpandedMetadata]
  );
  usePageTracking(pageTrackingMetadata);
  useInfoLogger(infoLoggerMetadata);

  const columns = useMemo(
    () =>
      sessionIdsSorted.map((sessionId) => ({
        sessionId,
      })),
    [sessionIdsSorted]
  );

  const anyAttachments = anyExampleHasAttachments(examples);

  const tab = useTab({ defaultIndex: 0, prefix: 'compare-experiment-' });

  const { metadataKey: groupByMetadataKey } = useGroupByMetadata();

  const TableComponent = groupByMetadataKey
    ? ExperimentsGroupByTable
    : DatasetSessionCompareTable;

  if (dataset.isLoading || sessions.isValidating || isExperimentViewLoading) {
    return <LinearProgress />;
  }

  return (
    <LogPageContext.Provider
      value={[DATADOG_PAGE_NAME, datadogExpandedMetadata]}
    >
      <RenderModeContext.Provider
        value={displaySettingsState.foreignDataRenderMode}
      >
        <div className="max-w-screen flex h-screen max-h-screen flex-col overflow-x-hidden">
          <div className="sticky left-0">
            <div className="mx-2 mt-2 px-2 pt-1">
              <Breadcrumbs />
            </div>
            <div className={cn('-mx-4 flex flex-col px-4')}>
              <div className="px-4">
                <div className="flex items-center justify-between gap-2">
                  <PageTitle
                    className={cn(
                      sessionIds.length === 1 && sessions.data?.rows?.[0]
                        ? '-ml-2 py-3'
                        : ''
                    )}
                  >
                    {pageTitle}
                  </PageTitle>
                  {datasetId ? (
                    <DatasetSessionControlHeader
                      sessions={sessions.data?.rows ?? []}
                      datasetShareToken={datasetShareToken}
                      datasetId={datasetId}
                      displaySettingsState={displaySettingsState}
                      datasetName={dataset.data?.name ?? ''}
                      setAddedSessions={setAddedSessions}
                      aggregateFeedbackKeys={aggregateFeedbackKeys ?? []}
                      pageTitle={pageTitleText}
                      noAttachments={!anyAttachments}
                      baselineSessionId={selectedBaselineSessionId}
                      setBaselineSessionId={setSelectedBaselineSessionId}
                      selectedFeedbackKey={
                        regressionsFilterFeedbackKey ?? undefined
                      }
                      setSelectedFeedbackKey={setRegressionsFilterFeedbackKey}
                      mutateDelta={feedbackDelta.mutate}
                      allowDownload={true}
                      isPairwise={!!comparativeExperiment}
                      onFeedbackConfigUpdate={() => {
                        mutate((key: FetcherParams) =>
                          key?.url?.includes(apiFeedbackConfigsPath)
                        );
                        feedbackDelta.mutate();
                      }}
                      onExperimentViewUpdate={(experimentView) => {
                        setLocalExperimentView(experimentView);
                      }}
                      experimentView={experimentView}
                      numSessions={sessionIds.length}
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {tab === CompareExperimentTabs.CHARTS ? (
            <DatasetSessionCompareBarCharts
              columns={columns}
              rows={sessions.data?.rows ?? []}
              selectedCharts={selectedCharts ?? []}
              loadingCharts={areExamplesLoading}
              datasetId={datasetId}
              barChartSpecs={displaySettingsState.barChartSpecs ?? []}
              aggregateMetadataKeys={aggregateMetadataKeys ?? []}
              setHoveredSession={setHoveredSession}
              selectedBaselineSessionId={selectedBaselineSessionId}
              setSelectedCharts={setSelectedCharts}
            />
          ) : null}

          {tab === CompareExperimentTabs.OUTPUTS ? (
            <TableComponent
              datasetId={datasetId}
              datasetShareToken={datasetShareToken}
              datasetExperimentView={experimentView}
              columns={columns}
              selectedBaselineSessionId={selectedBaselineSessionId}
              examples={examples}
              areExamplesLoading={areExamplesLoading}
              setExamplesSize={setExamplesSize}
              mutateInfiniteExamplesSwr={mutateInfiniteExamplesSwr}
              hasMoreToLoad={hasMoreToLoad}
              isNextPageValidating={isNextPageValidating}
              displaySettingsState={displaySettingsState}
              regressionsFilterFeedbackKey={regressionsFilterFeedbackKey}
              pageTitle={
                sessionIds.length > 1 ? 'Comparing Experiments' : undefined
              }
              datadogPageName={DATADOG_PAGE_NAME}
              setRegressionsFilteringToImprovements={
                setRegressionsFilteringToImprovements
              }
              regressionsFilteringToImprovements={
                regressionsFilteringToImprovements
              }
              setFilteringRegressionsOnSession={
                setFilteringRegressionsOnSession
              }
              filteringRegressionsOnSession={filteringRegressionsOnSession}
              filters={filterParams}
              setFilters={setFilterParamsState}
              useWindowScroll={false}
              addedSessions={addedSessions}
              sessions={sessions}
              hoveredSession={hoveredSession}
              setHoveredSession={setHoveredSession}
            />
          ) : null}
        </div>
      </RenderModeContext.Provider>
    </LogPageContext.Provider>
  );
};
